import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '../../../components/Badge';
import { Button } from '../../../components/Button';
import type { ComboboxOption } from '../../../components/Combobox';
import { ErrorNotice } from '../../../components/ErrorNotice';
import { LoadingIndicator } from '../../../components/Spinner';
import { formatDateTime } from '../../../i18n';
import { useChecklistTemplates } from '../../masterdata/checklistQueries';
import { nameOf, useAssignableUsers } from '../../users/queries';
import { useSetTicketParent, useTicket } from '../queries';
import { TicketCombobox } from '../TicketCombobox';
import type { LinkType, Task } from './api';
import {
  useAddChecklist,
  useChecklists,
  useClearReminder,
  useCreateLink,
  useCreateTask,
  useDeleteLink,
  useDeleteTask,
  useLinks,
  useReminder,
  useSetReminder,
  useTasks,
  useToggleChecklistItem,
  useUpdateTask,
} from './queries';
import { Select } from '../../../components/Select';

interface StructurePanelProps {
  ticketId: string;
  canWrite: boolean;
}

const LINK_TYPES: readonly LinkType[] = ['relates', 'duplicates', 'blocks', 'caused_by'];

/**
 * Teilaufgaben, Checklisten, Verknuepfungen und Wiedervorlage -- F1-16 bis
 * F1-18 und F1-22.
 *
 * Alle in einem Reiter, weil sie dieselbe Frage beantworten: Was gehoert noch
 * zu diesem Vorgang. Getrennte Reiter waeren mehrere Klicks fuer einen
 * Blick.
 *
 * **Ein Nachfolger mit offenem Vorgaenger laesst sich nicht starten** (K-13).
 * Die Oberflaeche zeigt das an; abgelehnt wird es serverseitig. Das
 * Ausgrauen ist Bequemlichkeit, keine Absicherung -- die Fachregel lebt im
 * Backend.
 */
export function StructurePanel({ ticketId, canWrite }: StructurePanelProps) {
  const { t, i18n } = useTranslation();

  const tasks = useTasks(ticketId);
  const checklists = useChecklists(ticketId);
  const links = useLinks(ticketId);
  const users = useAssignableUsers();
  // Die Vorlagen der Verwaltung -- Issue #4. Sie liessen sich anlegen und
  // pflegen, aber an kein Ticket haengen: Der Server konnte es die ganze
  // Zeit (``template_id`` an ``POST /tickets/{id}/checklists``), nur fehlte
  // hier das Bedienelement dafuer.
  const templates = useChecklistTemplates();

  const createTask = useCreateTask(ticketId);
  const updateTask = useUpdateTask(ticketId);
  const deleteTask = useDeleteTask(ticketId);
  const addChecklist = useAddChecklist(ticketId);
  const toggleItem = useToggleChecklistItem(ticketId);
  const createLink = useCreateLink(ticketId);
  const deleteLink = useDeleteLink(ticketId);

  const [taskTitle, setTaskTitle] = useState('');
  const [taskAssignee, setTaskAssignee] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [checklistName, setChecklistName] = useState('');
  const [checklistItems, setChecklistItems] = useState('');
  const [linkTarget, setLinkTarget] = useState<ComboboxOption | null>(null);
  const [linkType, setLinkType] = useState<LinkType>('relates');

  if (tasks.isPending || checklists.isPending || links.isPending) return <LoadingIndicator />;

  // Nach dem Ladezustand bleibt der Fehlerfall: Dann ist `data` leer, und
  // die Meldung steht unter dem jeweiligen Abschnitt.
  const taskList = tasks.data ?? [];
  const checklistList = checklists.data ?? [];
  const linkList = links.data ?? [];
  // Abgeschaltete Vorlagen werden nicht angeboten. Der Server nimmt sie
  // weiterhin an -- eine Kennung, die einmal gueltig war, soll nicht
  // nachtraeglich scheitern --, aber anbieten muss man sie nicht.
  const aktiveVorlagen = (templates.data ?? []).filter((template) => template.is_active);

  const blockedBy = (task: Task) =>
    task.depends_on_task_id
      ? taskList.find((candidate) => candidate.id === task.depends_on_task_id)
      : undefined;

  return (
    <div className="stack">
      {/* -- Teilaufgaben -------------------------------------------------- */}
      <section className="card">
        <h2>{t('structure.tasks')}</h2>

        {taskList.length === 0 ? (
          <p className="table__muted">{t('structure.noTasks')}</p>
        ) : (
          <ul className="task-list">
            {taskList.map((task) => {
              const predecessor = blockedBy(task);
              const blocked =
                predecessor !== undefined &&
                predecessor.status !== 'done' &&
                predecessor.status !== 'cancelled';

              return (
                <li key={task.id} className="task-list__item">
                  <input
                    type="checkbox"
                    checked={task.status === 'done'}
                    disabled={!canWrite || blocked}
                    aria-label={task.title}
                    onChange={(event) => {
                      updateTask.mutate({
                        taskId: task.id,
                        payload: { status: event.target.checked ? 'done' : 'open' },
                      });
                    }}
                  />
                  <span className={task.status === 'done' ? 'task-list__done' : undefined}>
                    {task.title}
                  </span>

                  {blocked ? (
                    <Badge tone="warning">
                      {t('structure.blockedBy', { title: predecessor.title })}
                    </Badge>
                  ) : null}

                  {task.assignee_id ? (
                    <span className="table__muted">{nameOf(users.data, task.assignee_id)}</span>
                  ) : null}

                  {task.due_at ? (
                    <span className="table__muted">
                      {formatDateTime(task.due_at, i18n.language)}
                    </span>
                  ) : null}

                  {canWrite ? (
                    <Button
                      variant="ghost"
                      onClick={() => {
                        deleteTask.mutate(task.id);
                      }}
                    >
                      {t('masterdata.delete')}
                    </Button>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}

        {canWrite ? (
          <div className="row">
            <input
              className="field__control"
              value={taskTitle}
              aria-label={t('structure.newTask')}
              placeholder={t('structure.newTask')}
              onChange={(event) => {
                setTaskTitle(event.target.value);
              }}
            />
            <Select
              className="field__control"
              aria-label={t('structure.taskAssignee')}
              value={taskAssignee}
              onChange={(event) => {
                setTaskAssignee(event.target.value);
              }}
            >
              <option value="">{t('tickets.unassigned')}</option>
              {(users.data ?? []).map((user) => (
                <option key={user.id} value={user.id}>
                  {user.full_name}
                </option>
              ))}
            </Select>
            <Button
              disabled={!taskTitle.trim()}
              busy={createTask.isPending}
              onClick={() => {
                createTask.mutate(
                  {
                    title: taskTitle.trim(),
                    description: '',
                    sort_order: 0,
                    assignee_id: taskAssignee || null,
                  },
                  {
                    onSuccess: () => {
                      setTaskTitle('');
                      setTaskAssignee('');
                    },
                  },
                );
              }}
            >
              {t('masterdata.add')}
            </Button>
          </div>
        ) : null}

        {tasks.isError ? <ErrorNotice error={tasks.error} /> : null}
        {createTask.isError ? <ErrorNotice error={createTask.error} /> : null}
        {updateTask.isError ? <ErrorNotice error={updateTask.error} /> : null}
      </section>

      {/* -- Checklisten --------------------------------------------------- */}
      <section className="card">
        <h2>{t('structure.checklists')}</h2>

        {checklistList.length === 0 ? (
          <p className="table__muted">{t('structure.noChecklists')}</p>
        ) : null}

        {checklistList.map((checklist) => (
          <div key={checklist.id} className="stack">
            <h3>{checklist.name}</h3>
            <ul className="task-list">
              {checklist.items.map((item) => (
                <li key={item.id} className="task-list__item">
                  <input
                    type="checkbox"
                    checked={item.is_done}
                    disabled={!canWrite}
                    aria-label={item.title}
                    onChange={(event) => {
                      toggleItem.mutate({ itemId: item.id, isDone: event.target.checked });
                    }}
                  />
                  <span className={item.is_done ? 'task-list__done' : undefined}>{item.title}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}

        {/* Aus einer Vorlage -- Issue #4. Zuerst, weil es der haeufigere Weg
            ist: Wer eine Vorlage angelegt hat, will sie benutzen und nicht
            ihre Punkte abschreiben. Die Punkte werden dabei **kopiert** --
            eine spaetere Aenderung an der Vorlage darf eine laufende
            Abarbeitung nicht umschreiben. */}
        {canWrite && aktiveVorlagen.length > 0 ? (
          <div className="row">
            <div className="field">
              <label className="field__label" htmlFor="checklist-template">
                {t('structure.checklistFromTemplate')}
              </label>
              <Select
                id="checklist-template"
                className="field__control"
                value={templateId}
                onChange={(event) => {
                  setTemplateId(event.target.value);
                }}
              >
                <option value="">{t('structure.chooseTemplate')}</option>
                {aktiveVorlagen.map((template) => (
                  <option key={template.id} value={template.id}>
                    {template.name}
                    {template.items.length > 0
                      ? ` (${t('structure.templateItemCount', {
                          count: template.items.length,
                        })})`
                      : ''}
                  </option>
                ))}
              </Select>
            </div>
            <Button
              disabled={!templateId}
              busy={addChecklist.isPending}
              onClick={() => {
                // Nur die Kennung: Name und Punkte kommen aus der Vorlage.
                // Sie hier zu wiederholen hiesse, dieselbe Angabe zweimal zu
                // fuehren, und die zweite waere irgendwann die falsche.
                addChecklist.mutate(
                  { template_id: templateId },
                  {
                    onSuccess: () => {
                      setTemplateId('');
                    },
                  },
                );
              }}
            >
              {t('structure.attachTemplate')}
            </Button>
          </div>
        ) : null}

        {canWrite && aktiveVorlagen.length === 0 && templates.isSuccess ? (
          // Kein stiller Verzicht: Wer die Vorlagen sucht, soll erfahren, wo
          // sie herkommen -- sonst sieht die fehlende Auswahl aus wie ein
          // Fehler.
          <p className="table__muted">{t('structure.noTemplates')}</p>
        ) : null}

        {canWrite ? (
          <div className="stack">
            <span className="field__label">{t('structure.checklistOwnItems')}</span>
            <input
              className="field__control"
              value={checklistName}
              aria-label={t('structure.checklistName')}
              placeholder={t('structure.checklistName')}
              onChange={(event) => {
                setChecklistName(event.target.value);
              }}
            />
            <textarea
              className="field__control field__control--area"
              rows={3}
              value={checklistItems}
              aria-label={t('structure.checklistItems')}
              placeholder={t('structure.checklistItemsHint')}
              onChange={(event) => {
                setChecklistItems(event.target.value);
              }}
            />
            <Button
              disabled={!checklistName.trim() || !checklistItems.trim()}
              busy={addChecklist.isPending}
              onClick={() => {
                addChecklist.mutate(
                  {
                    name: checklistName.trim(),
                    items: checklistItems
                      .split('\n')
                      .map((line) => line.trim())
                      .filter(Boolean),
                  },
                  {
                    onSuccess: () => {
                      setChecklistName('');
                      setChecklistItems('');
                    },
                  },
                );
              }}
            >
              {t('masterdata.add')}
            </Button>
          </div>
        ) : null}

        {checklists.isError ? <ErrorNotice error={checklists.error} /> : null}
        {addChecklist.isError ? <ErrorNotice error={addChecklist.error} /> : null}
      </section>

      {/* -- Verknuepfungen ------------------------------------------------ */}
      <section className="card">
        <h2>{t('structure.links')}</h2>

        {linkList.length === 0 ? (
          <p className="table__muted">{t('structure.noLinks')}</p>
        ) : (
          <ul className="task-list">
            {linkList.map((link) => (
              <li key={link.id} className="task-list__item">
                <Badge tone="neutral">
                  {t(`linkType.${link.link_type}`)}
                  {link.direction === 'inbound' ? ` (${t('structure.inbound')})` : ''}
                </Badge>
                {link.target_type === 'ticket' ? (
                  <Link to="/app/tickets/$ticketId" params={{ ticketId: link.target_id }}>
                    {link.target_label}
                  </Link>
                ) : (
                  <span>{link.target_label}</span>
                )}
                {canWrite && link.direction === 'outbound' ? (
                  <Button
                    variant="ghost"
                    onClick={() => {
                      deleteLink.mutate(link.id);
                    }}
                  >
                    {t('masterdata.delete')}
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        )}

        {canWrite ? (
          <div className="row">
            <Select
              className="field__control"
              aria-label={t('structure.linkType')}
              value={linkType}
              onChange={(event) => {
                setLinkType(event.target.value as LinkType);
              }}
            >
              {LINK_TYPES.map((value) => (
                <option key={value} value={value}>
                  {t(`linkType.${value}`)}
                </option>
              ))}
            </Select>
            <TicketCombobox
              value={linkTarget}
              onChange={setLinkTarget}
              excludeId={ticketId}
              ariaLabel={t('structure.linkTarget')}
              placeholder={t('structure.linkTargetHint')}
            />
            <Button
              disabled={linkTarget === null}
              busy={createLink.isPending}
              onClick={() => {
                if (linkTarget === null) return;
                createLink.mutate(
                  { link_type: linkType, target_type: 'ticket', target_id: linkTarget.id },
                  {
                    onSuccess: () => {
                      setLinkTarget(null);
                    },
                  },
                );
              }}
            >
              {t('masterdata.add')}
            </Button>
          </div>
        ) : null}

        {links.isError ? <ErrorNotice error={links.error} /> : null}
        {createLink.isError ? <ErrorNotice error={createLink.error} /> : null}
      </section>

      <ParentSection ticketId={ticketId} canWrite={canWrite} />

      <ReminderSection ticketId={ticketId} canWrite={canWrite} />
    </div>
  );
}

/**
 * Die Wiedervorlage -- F1-22, K-23.
 *
 * **Sie gehoert einem einzelnen Bearbeiter, nicht dem Vorgang.** Wer sich
 * etwas auf Wiedervorlage legt, legt es sich selbst hin; ein Kollege, der
 * dasselbe Ticket oeffnet, hat damit nichts zu tun. Der Server fuehrt sie
 * deshalb je Nutzer, und diese Maske zeigt nur die eigene.
 *
 * Das Datumsfeld traegt Uhrzeit: Eine Wiedervorlage "am Dienstag" ist keine.
 */
function ReminderSection({ ticketId, canWrite }: StructurePanelProps) {
  const { t, i18n } = useTranslation();
  const reminder = useReminder(ticketId);
  const setReminder = useSetReminder(ticketId);
  const clearReminder = useClearReminder(ticketId);

  const [when, setWhen] = useState('');
  const [note, setNote] = useState('');

  return (
    <section className="card">
      <h2>{t('structure.reminder')}</h2>

      {reminder.isPending ? <LoadingIndicator /> : null}
      {reminder.isError ? <ErrorNotice error={reminder.error} /> : null}

      {reminder.data ? (
        <div className="row">
          <p>
            {formatDateTime(reminder.data.remind_at, i18n.language)}
            {reminder.data.note ? ` — ${reminder.data.note}` : ''}
          </p>
          {canWrite ? (
            <Button
              variant="ghost"
              busy={clearReminder.isPending}
              onClick={() => {
                clearReminder.mutate();
              }}
            >
              {t('structure.clearReminder')}
            </Button>
          ) : null}
        </div>
      ) : (
        <p className="table__muted">{t('structure.noReminder')}</p>
      )}

      {canWrite ? (
        <div className="row">
          <input
            type="datetime-local"
            className="field__control"
            aria-label={t('structure.remindAt')}
            value={when}
            onChange={(event) => {
              setWhen(event.target.value);
            }}
          />
          <input
            className="field__control"
            aria-label={t('structure.reminderNote')}
            placeholder={t('structure.reminderNoteHint')}
            value={note}
            onChange={(event) => {
              setNote(event.target.value);
            }}
          />
          <Button
            disabled={when === ''}
            busy={setReminder.isPending}
            onClick={() => {
              // `datetime-local` liefert Ortszeit ohne Zone. `new Date` liest
              // sie in der Zone des Browsers -- genau die, in der der Anwender
              // sie gemeint hat -- und `toISOString` macht daraus UTC.
              setReminder.mutate(
                { remind_at: new Date(when).toISOString(), note },
                {
                  onSuccess: () => {
                    setWhen('');
                    setNote('');
                  },
                },
              );
            }}
          >
            {t('structure.setReminder')}
          </Button>
        </div>
      ) : null}

      {setReminder.isError ? <ErrorNotice error={setReminder.error} /> : null}
      {clearReminder.isError ? <ErrorNotice error={clearReminder.error} /> : null}
    </section>
  );
}

/**
 * Eltern-Kind-Beziehung -- F1-16, K-11.
 *
 * **Sie stand seit Phase 1 im Server und in keiner Maske.** Der Endpunkt gab
 * es, die Spalte auch -- aber ``parent_ticket_id`` erschien in keiner
 * Antwort, und damit konnte die Oberflaeche eine Beziehung weder anzeigen
 * noch setzen. Ohne sie war auch die Grossstoerung (F1-27) wirkungslos: Der
 * Abschluss vererbt sich an Kinder, die es nicht geben konnte.
 *
 * **Das Elternticket laesst sich erst schliessen, wenn alle Kinder
 * geschlossen sind** -- ausser es ist eine Grossstoerung, dann gibt es
 * seinen Abschluss an sie weiter. Die Regel setzt der Server durch; hier
 * steht sie als Hinweis, damit niemand vor einem Schliessen-Knopf steht, der
 * nicht will.
 */
function ParentSection({ ticketId, canWrite }: StructurePanelProps) {
  const { t } = useTranslation();
  const ticket = useTicket(ticketId);
  const setParent = useSetTicketParent(ticketId);

  const [candidate, setCandidate] = useState<ComboboxOption | null>(null);

  const parentId = ticket.data?.parent_ticket_id ?? null;
  const children = ticket.data?.children ?? [];

  return (
    <section className="card">
      <h2>{t('structure.hierarchy')}</h2>

      {ticket.isError ? <ErrorNotice error={ticket.error} /> : null}

      {parentId ? (
        <p>
          {t('structure.parentIs')}{' '}
          <Link to="/app/tickets/$ticketId" params={{ ticketId: parentId }}>
            {parentId}
          </Link>
          {canWrite ? (
            <>
              {' '}
              <Button
                variant="ghost"
                busy={setParent.isPending}
                onClick={() => {
                  setParent.mutate(null);
                }}
              >
                {t('structure.detach')}
              </Button>
            </>
          ) : null}
        </p>
      ) : (
        <p className="table__muted">{t('structure.noParent')}</p>
      )}

      {children.length > 0 ? (
        <>
          <p className="field__label">{t('structure.children')}</p>
          <ul className="stack">
            {children.map((child) => (
              <li key={child.id}>
                <Link to="/app/tickets/$ticketId" params={{ ticketId: child.id }}>
                  <span className="table__number">{child.ticket_number}</span> {child.title}
                </Link>
              </li>
            ))}
          </ul>
          <span className="field__hint">{t('structure.childrenHint')}</span>
        </>
      ) : null}

      {canWrite && parentId === null ? (
        <div className="row">
          <TicketCombobox
            value={candidate}
            onChange={setCandidate}
            excludeId={ticketId}
            ariaLabel={t('structure.parentTarget')}
            placeholder={t('structure.parentTargetHint')}
          />
          <Button
            disabled={candidate === null}
            busy={setParent.isPending}
            onClick={() => {
              if (candidate === null) return;
              setParent.mutate(candidate.id, {
                onSuccess: () => {
                  setCandidate(null);
                },
              });
            }}
          >
            {t('structure.attach')}
          </Button>
        </div>
      ) : null}

      {setParent.isError ? <ErrorNotice error={setParent.error} /> : null}
    </section>
  );
}
